<template><div><h1 id="projektfinanzierung" tabindex="-1"><a class="header-anchor" href="#projektfinanzierung"><span>Projektfinanzierung</span></a></h1>
<h2 id="_1-projekte-ubersicht" tabindex="-1"><a class="header-anchor" href="#_1-projekte-ubersicht"><span>1. Projekte Übersicht</span></a></h2>
<p>Jeder Betrag, der im Rahmen einer Spendenakquisition einbezahlt wird, kann auf ein bestimmtes Projekt verbucht werden. Damit wird ein kampagnen- bzw. projektgestütztes Spendenmarketing möglich. In den Projekten werden alle Beträge für eine bestimmte Zweckbindung zusammengefasst. Im Projekt werden die Sammelziele (pro Periode) definiert und der Stand der Spendeneingänge ist jederzeit online sichtbar.</p>
<p>Bei Spenden von Grossgönnern können neben den Ereignissen auch Aktionen erfasst. Diese bilden die Grundlage für die Abbildung und Berechnung der zum Projekt ausgewiesenen Beträge.</p>
<p>Es ist zu unterscheiden zwischen Buchungen (Spenden), Ereignissen und Projekten. Zahlungen sind pflichtig zu einem konkreten Ereignis zu buchen. Die Projekte sind als eine höhere Ebene zu verstehen, welche die einzelnen Ereignisse miteinander verbindet.</p>
<p><img src="@source/basics/i/projektfinanzierung/1_projekte.png" alt=""></p>
<h3 id="_1-1-dokumentation" tabindex="-1"><a class="header-anchor" href="#_1-1-dokumentation"><span>1.1 Dokumentation</span></a></h3>
<p>Die technischen Daten sind in der Dokumentation zu finden:</p>
<ul>
<li><RouteLink to="/referenz/refs/ft.refdata.projekte.html">Projekte</RouteLink></li>
</ul>
<h3 id="_1-2-projekte" tabindex="-1"><a class="header-anchor" href="#_1-2-projekte"><span>1.2 Projekte</span></a></h3>
<p>(Menupfad: «Projektfinanzierung/Projekte»)</p>
<p>Projekte sind die Hauptmaske zur Erfassung und Pflege der eigentlichen Projekte.</p>
<p>Es gibt zwei verschiedene Versionen von Projekten: die Standardversion und die vereinfachte Version. Diese Versionen sind kundenspezifisch angepasst und können in nicht gleichzeitig eingesetzt werden. Beide Versionen sind als separate Module konfiguriert.</p>
<p>Der Hauptunterschied zwischen den Versionen ist folgender</p>
<ul>
<li>Die Standardversion ist detaillierter und listet für jede Periode alle spezifischen Gönner und Beträge auf.</li>
<li>In der vereinfachten Version werden die Beträge der Spender nur kumuliert ausgewiesen.</li>
</ul>
<h4 id="_1-2-1-projekt-–-standardversion" tabindex="-1"><a class="header-anchor" href="#_1-2-1-projekt-–-standardversion"><span>1.2.1 Projekt – Standardversion</span></a></h4>
<p>In der obersten Maske befinden sich alle kumulierten Informationen zu den Projekten. Ganz oben sind allgemeine Informationen wie Projektname und Beschreibung, und ebenso weitere Parameter, die Einfluss auf die Funktionalität haben:</p>
<ul>
<li><strong>Status:</strong> In Vorbereitung, laufend oder beendet</li>
<li><strong>Projekttyp:</strong> Je nach Projekttyp stehen verschiedene Aktionen zur Verfügung</li>
<li><strong>Laufzeit von/bis:</strong> Dauer des Projektes</li>
<li><strong>betreut durch</strong> : Interner Mitarbeiter/User, der das Projekt betreut. In Lookup für funtrade-User zur Auswahl.</li>
<li><strong>Priorität</strong> : normal / hoch / tief</li>
</ul>
<p><img src="@source/basics/i/projektfinanzierung/projekte-v2-projekt.png" alt=""></p>
<p>In den mittleren Masken gibt es drei Bereiche</p>
<ul>
<li>Programm</li>
<li>Finanzierungstatus</li>
<li>Merkmale / Zusatzinformationen</li>
</ul>
<p><strong>Programm</strong> - Mit Informationen zum Ablauf des Projekts</p>
<ul>
<li><strong>Status</strong> : aktueller Status vom Programm (in Planung / in Realisierung / in Verlängerung / abgeschlossen / unterbrochen / annuliert)</li>
<li><strong>Verantw. Programm</strong> : Verantwortliche Person für das Programm</li>
<li><strong>Verantw. Realisierung</strong> : (momentan nicht benutzt)</li>
<li><strong>Projektstart</strong> : Start-Datum des Programms</li>
<li><strong>Ende</strong> : Ende-Datum des Programms</li>
</ul>
<p><strong>Finanzierungstatus</strong> - Mit einer Übersicht zu Projektstand und Zielerreichung.</p>
<ul>
<li><strong>Projektziel</strong> : Projektziel (netto) mit kumuliertem Betrag aller Perioden</li>
<li><strong>zugesagte Beträge</strong> : Summe aller zugesagten Beträge</li>
<li><strong>total Fehlbetrag/Überschluss</strong> : Differenz zwischen dem Zielbetrag und den zugesagten Beträgen</li>
<li><strong>pendente Anfragen</strong> : Summe aller pendenten Beträge</li>
</ul>
<p><strong>Merkmale / Zusatzinformationen</strong> - Hier werden die aktuellen Merkmale und Zusatzinformationen zum Projekt nach der Konfiguration im Systemparameter «projekt-info» dargestellt.</p>
<p>In den untersten Masken gibt es sieben Bereiche:</p>
<ol>
<li>
<p><strong>Finanzierung pro Periode</strong> – Liste von Aktionen zum Projekt, die zugesagten und/oder pendenten Betrag haben. Die Liste ist in einer Baumstruktur nach Periode geordnet.</p>
<p>Spalten in der Liste:</p>
<ul>
<li>
<p><strong>Periode</strong> – aktuelle Periode der Aktion</p>
</li>
<li>
<p><strong>Zielbetrag</strong> – für die Periode festgelegter Zielbetrag</p>
</li>
<li>
<p><strong>Gönner</strong> – Person der Aktion</p>
</li>
<li>
<p><strong>zugesagt</strong> – Betrag bei der Aktion, die Schalter „zugesagt&quot; eingesetzt hat</p>
</li>
<li>
<p><strong>pendente Anfragen</strong> - Betrag bei der Aktion, die Schalter „eingereicht&quot; eingesetzt hat wenn es für das Projekt keine andere Aktion „zugesagt&quot; und „abgesagt&quot; mit der gleichen Sammelnummer gibt.</p>
</li>
<li>
<p><strong>Differenz</strong> – Differenze zwischen „zugesagt&quot; und „Zielbetrag&quot;</p>
</li>
<li>
<p><strong>kumuliert</strong> – kumulierter Wert der Differenzen</p>
</li>
</ul>
</li>
</ol>
<p><img src="@source/basics/i/projektfinanzierung/projekte-v2-finanz-periode.png" alt=""></p>
<ol start="2">
<li>
<p><strong>Detail pro Periode</strong> – Liste aller Aktionen für das Projekt. Die Liste ist in einer Baumstruktur nach Periode geordnet.</p>
<p>Spalten in der Liste:</p>
<ul>
<li>
<p><strong>Periode</strong> – aktuelle Periode der Aktion</p>
</li>
<li>
<p><strong>Gönner</strong> – Person der Aktion</p>
</li>
<li>
<p><strong>eingereicht</strong> - Betrag bei der Aktion, die Schalter „eingereicht&quot; hat</p>
</li>
<li>
<p><strong>zugesagt</strong> – Betrag bei der Aktion, die Schalter „zugesagt&quot; hat</p>
</li>
<li>
<p><strong>abgesagt</strong> - Betrag bei der Aktion, die Schalter „abgesagt&quot; hat</p>
</li>
<li>
<p><strong>pendent</strong> - Betrag bei der Aktion, die Schalter „eingereicht&quot; hat wenn es für das Projekt keine andere Aktion „zugesagt&quot; und „abgesagt&quot; mit der gleichen Sammelnummer gibt.</p>
</li>
<li>
<p><strong>bezahlt</strong> - Betrag bei der Aktion, die im Systemparameter „terkopfProjakt&quot; definiert ist</p>
</li>
<li>
<p><strong>Zielbetrag</strong> – für die Periode festgelegter Zielbetrag</p>
</li>
<li>
<p><strong>Resultat</strong> – Differenz zwischen „zugesagt&quot; und „Zielbetrag&quot;</p>
</li>
</ul>
</li>
</ol>
<p><img src="@source/basics/i/projektfinanzierung/projekte-v2-detail-periode.png" alt=""></p>
<ol start="3">
<li><strong>Aktionen</strong> – Liste alle Aktionen, die waren zum Projekt erfasst.</li>
<li><strong>Rollen</strong> – spezifische Information. Es können, ausser Betreuer, mehrere Personen an dem Projekt beteiligt sein.</li>
<li><strong>Merkmale</strong>  <strong>–</strong> Liste von Merkmalen, die mit dem Projekt verlinkt sind.</li>
<li><strong>Ereignisse –</strong> Liste von Ereignissen, die mit dem Projekt verlinkt sind.</li>
<li><strong>Projektänderungen</strong> – Liste aller Änderungen in bestimmten Feldern des Projekts</li>
</ol>
<p><img src="@source/basics/i/projektfinanzierung/projekte-v2-aenderungen.png" alt=""></p>
<h4 id="_1-2-1-1-projekt-erfassen" tabindex="-1"><a class="header-anchor" href="#_1-2-1-1-projekt-erfassen"><span>1.2.1.1 Projekt erfassen</span></a></h4>
<p>Zunächst muss man ein neues Projekt für einen bestimmten Zweck definiert werden. Dabei lassen sich auch die relevanten Projektmerkmale festlegen.</p>
<p><img src="@source/basics/i/projektfinanzierung/projekte-v2-projekt-erfassen.png" alt=""></p>
<p>Die Werte in den Bereichen Fundraising und Programm haben hier nur informativen Zweck und keine spezielle Funktionalität.</p>
<p>Vor Beginn des Projekts muss der Zielbetrag des Projekts definiert werden. Der Zielbetrag des Projekts lässt sich separat pro Periode in der Maske «Finanzierung pro Periode» oder «Detail pro Periode» definieren.</p>
<p>In der Standardversion werden die Änderungen von bestimmten Feldern des Projekts protokolliert. Diese Änderungen sind deshalb dann in der Maske «Projektänderungen» sichtbar.</p>
<p>Die einzelnen Aktionen für das Projekt lassen sich entweder manuell oder automatisch erfassen.</p>
<p>Nach Eingabe einer Aktion werden auch die einzelnen Beträge in der Maske „Finanzierung pro Periode&quot;, „Detail pro periode&quot; und „Projekt&quot; entsprechend den Aktionsparametern für den Projektumsatz aktualisiert.</p>
<p>Für automatisch erfasste Aktionen müssen zuerst auch die spezifischen Ereignisse in der Stammtabelle „Ereignisse&quot; definiert und mit dem Projekt verbunden werden.</p>
<p>Der Prozess wird in Kapitel <strong>Aktionen erfassen</strong> detailliert beschrieben.</p>
<p><img src="@source/basics/i/projektfinanzierung/projekte-v2-projektziel.png" alt=""></p>
<h3 id="_1-2-2-projekt-–-vereinfachte-version" tabindex="-1"><a class="header-anchor" href="#_1-2-2-projekt-–-vereinfachte-version"><span>1.2.2 Projekt – Vereinfachte Version</span></a></h3>
<p><img src="@source/basics/i/projektfinanzierung/projekte-haupt.png" alt=""></p>
<p>In der obersten Maske befinden sich alle kumulierten Informationen über Projekte. Ganz oben sind allgemeine Informationen wie Projektname oder Beschreibung sowie Parameter, die Einfluss auf Funktionalität haben:</p>
<ul>
<li>
<p><strong>Status:</strong> In Vorbereitung, laufend oder beendet ** **</p>
</li>
<li>
<p><strong>Projekttyp:</strong> Je nach Projekttyp stehen verschiedene Aktionen zur Verfügung</p>
</li>
<li>
<p><strong>Laufzeit von/bis:</strong> Dauer des Projektes</p>
</li>
<li>
<p><strong>betreut durch:</strong> Interner Mitarbeiter/User, der das Projekt betreut. In Lookup für funtrade-User zur Auswahl.</p>
</li>
</ul>
<p>Weitere Informationen sind aufgeteilt in:</p>
<ol>
<li>
<p><strong>Projektziel</strong> - Gesamt-Projektziel, basierend auf den Jahreszielen, abzüglich der vorbestehenden Erträge.</p>
<ul>
<li><strong>Projektziel</strong> : Summe der einzelnen Projektziele pro Periode, die unter <em>Umsatz pro Jahr</em> eingetragen werden.</li>
<li><strong>Vorbestehender Ertrag:</strong> Bereits realisierter Ertrag, der nicht direkt aus diesem Projekt stammt. Wird vom Projektziel abgezogen.</li>
<li><strong>Projektziel Netto</strong> : Projektziel Netto = [Projektziel] - [Vorbestehender Ertrag]</li>
</ul>
</li>
<li>
<p><strong>Einnahmen</strong> - Übersicht der Einnahmen summiert aus allen Perioden</p>
<ul>
<li>
<p><strong>Kollektivmarketing:</strong> Eingang von Zahlungen auf das Projekt, welche über ein Kollektivmarketing-Ereignis akquiriert worden sind. Entscheidend für die Verbuchung der Zahlungen unter Kollektivmarketing wird über die Ereignisdefinition sowie die Verknüpfung des Ereignisses mit dem Projekt gesteuert.</p>
</li>
<li>
<p><strong>Individualmarketing:</strong> Eingang von Zahlungen auf das Projekt, welche über ein Individualmarketing-Ereignis akquiriert wurden. Die Verbuchung der Zahlungen unter Individualmarketing wird über die Ereignisdefinition sowie die Verknüpfung des Ereignisses mit dem Projekt gesteuert.</p>
</li>
<li>
<p><strong>Weitere Quellen:</strong> Summe der Beträge gemäss Tabelle „Weitere Quellen zu Projekt und Jahr&quot;, welche manuell erfasst oder „Aktionen&quot; mit Zuordnung &quot;weitere Quellen&quot; zugeordnet worden sind.</p>
</li>
<li>
<p><strong>Einnahmen :</strong> Einnahmen Total = [Summe Kollektivmarketing] + [Summe Individualmarketing] + [Summe weitere Quellen]</p>
</li>
</ul>
</li>
<li>
<p><strong>Aktionen</strong> - Die Beträge werden berechnet aus den mit dem Projekt verknüpften Aktionen. In der <RouteLink to="/referenz/refs/ft.refdata.aktionscodes.html">Aktionen-Stammtabelle</RouteLink> wird pro Aktionstyp definiert, ob der eingetragene Betrag im Projekt-Kopf angezeigt werden soll oder nicht.</p>
</li>
<li>
<p><strong>Projektstand</strong> - Übersicht Projektstand/Zielerreichung.</p>
<ul>
<li>
<p><strong>Projektziel</strong> – Projektziel (netto). Gleiches Feld wie im Frame Projektziel.</p>
</li>
<li>
<p><strong>Einnahmen</strong> – Einnahmen total. Gleiches Feld wie im Frame &quot;Einnahmen&quot;.</p>
</li>
<li>
<p><strong>Prognose (Einnahmen)</strong> – Einnahmen berechnet mit Prognosebetrag aus den Aktionen.</p>
</li>
<li>
<p><strong>Differenz zum Ziel</strong> – Differenz zum Ziel= [Einnahmen] - [Projektziel]</p>
</li>
<li>
<p><strong>Kosten</strong> – Kosten, summiert aus den <RouteLink to="/referenz/refs/ft.refdata.ereigkosten.html">Ereigniskosten</RouteLink> der mit dem Projekt verknüpften Ereignisse.</p>
</li>
<li>
<p><strong>Ertrag</strong> – Ertrag = [Einnahmen] - [Kosten].</p>
</li>
<li>
<p><strong>Diff. zum Ziel netto</strong> – Diff. zum Ziel netto = [Diff. zum Ziel] - [Ausgaben Total]</p>
</li>
<li>
<p><strong>ROI</strong> – Der Return on Investment berechnet sich wie folgt, Gewinnanteil / Kapitaleinsatz
bezogen auf Ereignisse, resp. Ereignisebenen: ROI = [Ertrag] / [Kosten]</p>
</li>
</ul>
</li>
<li>
<p><strong>Merkmale / Zusatzinformationen</strong> - Hier sind die aktuellen Merkmale und Zusatzinformationen zum Projekt nach der Konfiguration im Systemparameter «projekt-info» dargestellt.</p>
</li>
</ol>
<p>In der mittleren Maske gibt es fünf Tabs:</p>
<ol>
<li><strong>Projektzahlen pro Periode</strong> – Im Vergleich zur obersten Ebene sind hier die einzelnen Beträge nach Jahren gegliedert.</li>
<li><strong>Ereignisse</strong> – Liste der Ereignisse, die mit Projekt verlinkt sind.</li>
<li><strong>Aktionen</strong> – Liste aller Aktionen, die zum Projekt erfasst worden sind.</li>
<li><strong>Rollen</strong> – Spezifische Informationen zu den Projektbeteiligten. Es können nebst Betreuer mehrere Personen an einem Projekt beteiligt sein.</li>
<li><strong>Merkmale –</strong> Liste der Merkmale, die mit dem Projekt verlinkt sind.</li>
</ol>
<p>In der untersten Maske gibt es drei Tabs:</p>
<ol>
<li><strong>Umsatz pro Jahr Detail</strong> – Hier werden die einzelnen Beträge pro Jahr in demselben Format wie in der obersten Maske angezeigt.</li>
<li><strong>weitere Quellen zu Projekt und Jahr</strong> – Hier lassen sich weitere Quelle pro Jahr definieren, weil diese sind nicht automatisch aus Ereignissen summiert werden.</li>
<li><strong>Aktionen</strong> – Liste aller Aktionen, die zum Projekt im Jahr erfasst worden sind.</li>
</ol>
<h4 id="_1-2-2-1-projekt-erfassen" tabindex="-1"><a class="header-anchor" href="#_1-2-2-1-projekt-erfassen"><span>1.2.2.1 Projekt erfassen</span></a></h4>
<p>Zunächst muss ein neues Projekt für einen bestimmten Zweck definiert werden. Dabei lassen sich auch die relevanten Projektmerkmale festlegen.</p>
<p>Vor Beginn des Projekts muss der Zielbetrag des Projekts definiert werden. Der Zielbetrag des Projekt lässt sich auch separat pro Periode definieren.</p>
<p><img src="@source/basics/i/projektfinanzierung/projekte-neues-projekt.png" alt=""></p>
<p><img src="@source/basics/i/projektfinanzierung/projekte-projektziel.png" alt=""></p>
<p>Jetzt lassen sich einzelne Aktionen für das Projekt entweder manuell oder automatisch erfassen.</p>
<p>Nach Eingabe einer Aktion werden auch die einzelnen Beträge in der Maske „Umsatz pro Jahr&quot; und „Projekt&quot; entsprechend den Aktionsparametern für den Projektumsatz aktualisiert.</p>
<p>Für automatisch erfasste Aktionen müssen zuerst auch die spezifischen Ereignisse in der Stammtabelle „Ereignisse&quot; definiert und mit dem Projekt verbunden werden.</p>
<p>Der Prozess wird in Kapitel  <strong>Aktionen erfassen</strong> detailliert beschrieben.</p>
<h2 id="_2-verarbeitungslaufe" tabindex="-1"><a class="header-anchor" href="#_2-verarbeitungslaufe"><span>2 Verarbeitungsläufe</span></a></h2>
<h3 id="_2-1-projektumsatze-aktualisieren" tabindex="-1"><a class="header-anchor" href="#_2-1-projektumsatze-aktualisieren"><span>2.1 Projektumsätze aktualisieren</span></a></h3>
<p>Unter „Projektfinanzierung/Verarbeitungläufe/Projektumsätze aktualisieren&quot; lassen sich</p>
<p>Projektumsätze aktualisieren. Dieser Job löscht alle nicht verwendeten Einträge mit Nullbeträgen.</p>
<p><img src="@source/basics/i/projektfinanzierung/projekte-umsatz-aktual.png" alt=""></p>
<h2 id="_3-auswertungen" tabindex="-1"><a class="header-anchor" href="#_3-auswertungen"><span>3 Auswertungen</span></a></h2>
<h3 id="_3-1-aktionauswertung-projekte" tabindex="-1"><a class="header-anchor" href="#_3-1-aktionauswertung-projekte"><span>3.1 Aktionauswertung Projekte</span></a></h3>
<p>Die Auswertung listet alle Projekte in einem definierten Zeitraum auf.</p>
<p>Die Auswertung verarbeitet die Aktionen nach Periode oder nach Datum der Aktionen und die Ergebnisse können nach Projektgruppe gruppiert werden. Hinweis: Eine Projektgruppe beim Projekt wird durch ein Projektgruppenmerkmal definiert.</p>
<p><img src="@source/basics/i/projektfinanzierung/projekte-auswertung-projekte.png" alt=""></p>
<h3 id="_3-2-aktionauswertung-projektfinanzierer" tabindex="-1"><a class="header-anchor" href="#_3-2-aktionauswertung-projektfinanzierer"><span>3.2 Aktionauswertung Projektfinanzierer</span></a></h3>
<p>Die Auswertung listet Beträge (pro Gönner/Projektfinanzierer), aufgeteilt nach Aktionen auf.</p>
<p>Die Auswertung verarbeitet die Aktionen nach der Periode oder nach dem Datum der Aktionen und die Ergebnisse können nach Projekt, Projektfinanzierer oder nach Branche der Projektfinanzierer gruppiert werden.</p>
<p><img src="@source/basics/i/projektfinanzierung/projekte-auswertung-finanz.png" alt=""></p>
<h2 id="_4-stammtabellen" tabindex="-1"><a class="header-anchor" href="#_4-stammtabellen"><span>4 Stammtabellen</span></a></h2>
<h3 id="_4-1-projektmerkmale" tabindex="-1"><a class="header-anchor" href="#_4-1-projektmerkmale"><span>4.1 Projektmerkmale</span></a></h3>
<p>Die Stammtabelle definiert die Massnahmen, die die Art der einzelnen Projekte zum Ausdruck bringen.</p>
<p>Die Merkmale sind frei definierbar. Ein Merkmale dient als Projektgruppenkennzeichen. Dieses Merkmal für die Projektgruppe ist im Systemparameter «projgrpMerkmalId» definiert.</p>
<p><img src="@source/basics/i/projektfinanzierung/projekte-merkmale.png" alt=""></p>
<h3 id="_4-2-projektrollen" tabindex="-1"><a class="header-anchor" href="#_4-2-projektrollen"><span>4.2 Projektrollen</span></a></h3>
<p>In dieser Tabelle, sind die Rollen der an den Projekten beteiligten Benutzer definiert.</p>
<p><img src="@source/basics/i/projektfinanzierung/projekte-rollen.png" alt=""></p>
<h3 id="_4-3-projekttypen" tabindex="-1"><a class="header-anchor" href="#_4-3-projekttypen"><span>4.3 Projekttypen</span></a></h3>
<p>In dieser Tabelle werden Einstellungen definiert, die für jedes verknüpfte Projekt gelten. Jedes Projekt muss mit einem Projekttyp verknüpft sein.</p>
<p>Zu jedem Projekttyp ist auch ein Set von Aktionen definiert, die beim Projekt benutzt werden können.</p>
<p><img src="@source/basics/i/projektfinanzierung/projekte-typen.png" alt=""></p>
</div></template>


